import React, { useState, useEffect } from 'react';
import { useAuth } from '../authentication/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditsDisplay } from '../components/CreditsDisplay';
import { paymentApi } from '../services/paymentApi';
import { StripeWrapper } from '../components/StripeWrapper';
import { PaymentForm } from '../payment/PaymentForm';
import { FREE_RESEARCH_LIMITS, SUBSCRIPTION_LIMITS } from '../payment/PricingUtils';
import ManageSharedLinks from '../components/ManageSharedLinks'; // <-- NEW IMPORT
import '../index.css';
import '../styles/AccountPage.css';


function MyAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    firstName, 
    email, 
    changePassword, 
    lastFreeResearch,
    hasActiveSubscription,
    subscriptionUntil,
    subscriptionCanceledDate,
    credits,
    updateUserAttributes,
    deleteAccount
  } = useAuth();
  
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSubscribeForm, setShowSubscribeForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionCanceledDateState, setSubscriptionCanceledDate] = useState(subscriptionCanceledDate);
  const [creditsState, setCredits] = useState(credits);
  const [subscriptionUntilState, setSubscriptionUntil] = useState(subscriptionUntil);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  // Check for message in location state when component mounts
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
    
    // Show subscription form if coming from pricing page with subscribe param
    if (location.state?.subscribe) {
      setShowSubscribeForm(true);
    }
  }, [location]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    try {
      await changePassword(oldPassword, newPassword);
      setMessage('Password changed successfully!');
      setShowChangePassword(false);
      setOldPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    }
  };

  const getNextFreeResearchDate = () => {
    if (!lastFreeResearch) return 'Available now';
    const lastDate = new Date(lastFreeResearch);
    const nextDate = new Date(lastDate.getTime() + (7 * 24 * 60 * 60 * 1000));
    return nextDate > new Date() ? 
      `Available on ${nextDate.toLocaleDateString()}` : 
      'Available now';
  };

  const handleDeleteAccount = async () => {
    setDeleteInProgress(true);
    setDeleteError('');
    
    try {
      await deleteAccount();
      // Redirect to home page after successful deletion
      navigate('/');
    } catch (error) {
      setDeleteError('Failed to delete account. Please try again.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will still have access until your current billing period ends.')) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      await paymentApi.cancelSubscription();
      setMessage('Your subscription has been canceled. You will still have access until the end of your current billing period.');
      
      const updatedUserData = await updateUserAttributes();
      
      if (updatedUserData) {
        setSubscriptionCanceledDate(updatedUserData.subscriptionCanceledDate);
        setCredits(updatedUserData.credits);
        setSubscriptionUntil(updatedUserData.subscriptionUntil);
      }
    } catch (err) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!window.confirm('Would you like to reactivate your subscription? You will continue to be billed after your current period ends.')) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await paymentApi.reactivateSubscription();
      setMessage(result.message || 'Your subscription has been reactivated and will automatically renew.');
      
      const updatedUserData = await updateUserAttributes();
      
      if (updatedUserData) {
        setSubscriptionCanceledDate(updatedUserData.subscriptionCanceledDate);
        setCredits(updatedUserData.credits);
        setSubscriptionUntil(updatedUserData.subscriptionUntil);
      }
    } catch (err) {
      console.error('Reactivation error:', err);
      
      if (err.message?.includes('not scheduled to be canceled') || err.alreadyActive) {
        setMessage('Your subscription is already active. The display will be updated.');
        
        const updatedUserData = await updateUserAttributes();
        if (updatedUserData) {
          setSubscriptionCanceledDate(null);
          setCredits(updatedUserData.credits);
          setSubscriptionUntil(updatedUserData.subscriptionUntil);
        }
      } else {
        setError(err.message || 'Failed to reactivate subscription');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshSubscriptionStatus = async () => {
    setIsLoading(true);
    try {
      await updateUserAttributes();
      setMessage("Account information refreshed successfully.");
    } catch (error) {
      setError("Failed to refresh subscription status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscriptionCheckout = async () => {
    setError('');
    setIsLoading(true);
    try {
      const sessionId = await paymentApi.createSubscription();
      setIsLoading(false);
      return sessionId;
    } catch (err) {
      setError(err.message || 'Failed to create subscription');
      setIsLoading(false);
      throw err;
    }
  };

  const handlePaymentError = (error) => {
    setError(error.message || 'Payment processing failed');
    setIsLoading(false);
  };

  const renderChangePassword = () => (
    <div className="account-card">
      <h2>Change Password</h2>
      <form onSubmit={handleChangePassword}>
        <div className="form-group">
          <label htmlFor="oldPassword">Current Password</label>
          <input 
            type="password" 
            id="oldPassword" 
            value={oldPassword} 
            onChange={(e) => setOldPassword(e.target.value)} 
            required 
            placeholder="Enter your current password"
          />
          
          <label htmlFor="newPassword">New Password</label>
          <input 
            type="password" 
            id="newPassword" 
            value={newPassword} 
            onChange={(e) => setNewPassword(e.target.value)} 
            required 
            placeholder="Enter your new password"
          />
        </div>
        
        <div className="button-group">
          <button type="submit" className="button standout-button">
            Update Password
          </button>
          <button 
            type="button" 
            onClick={() => setShowChangePassword(false)} 
            className="button secondary-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="account-page">
      <div className="account-container">
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        
        <h1 className="account-title">My Account</h1>
        
        <div className="account-card">
          <div className="account-card-header">
            <h2 className="account-card-title">Profile Information</h2>
            <button 
              className="button primary-button account-button"
              onClick={handleRefreshSubscriptionStatus}
              disabled={isLoading}
              style={{
                borderRadius: '5px', 
                background: "linear-gradient(135deg, var(--tertiary-color), var(--secondary-color))", 
                fontWeight: '600', 
                boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                transition: "all 0.3s ease",
                transform: "translateY(0)",
                border: "none",
                position: "relative",
                overflow: "hidden",
                color: 'white'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-3px)"}
              onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              {isLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
          </div>
          
          <div className="account-section">
            <div className="account-info-box">
              <div style={{ marginBottom: "1rem" }}>
                <p className="info-label">Name</p>
                <p className="info-value">{firstName || 'Not set'}</p>
              </div>
              <div>
                <p className="info-label">Email</p>
                <p className="info-value">{email || 'Not set'}</p>
              </div>
            </div>
            
            <div className="account-info-box">
            {hasActiveSubscription ? (
              <div style={{ marginBottom: "1rem" }}>
                <p className='info-label'>Account Status</p>
                <p className='info-value'>Premium Account</p>           
              </div> 
              ) : (
              <div style={{ marginBottom: "1rem" }}>
                <p className='info-label'>Account Status</p>               
                <p className='info-value'>Free Account</p>              
              </div>
              )}
              <div>
                <p className="info-label">Account Security</p>
                <a 
                  href="/account" 
                  className="info-value"
                  style={{ textDecoration: 'underline', color: 'var(--tertiary-color)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowChangePassword(!showChangePassword);
                  }}
                >
                  Change Password
                </a>
              </div>
              
            </div>
          </div>
        </div>
  
        {showChangePassword && renderChangePassword()}
  
        <div className="account-card">
          {/* MODIFIED: Added header container to position the new button */}
          <div className="account-card-header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="account-card-title" style={{ margin: 0 }}>Research Account</h2>
            <ManageSharedLinks />
          </div>
          
          
            <CreditsDisplay />
          
          
          {hasActiveSubscription ? (
            <div style={{ borderTop: "1px solid #eee", paddingTop: "1.5rem" }}>
              <div className="subscription-header">
                <h3 className="subscription-title">Premium Subscription</h3>
                {subscriptionCanceledDateState ? (
                  <span className="subscription-badge badge-canceled">CANCELED</span>
                ) : (
                  <span className="subscription-badge badge-active">ACTIVE</span>
                )}
              </div>
              
              <div className="subscription-grid">
                {subscriptionCanceledDateState ? (
                  <div>
                    <p className="info-label">Access Until</p>
                    <p className="info-value">
                      {new Date(subscriptionUntilState).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="info-label">Renews On</p>
                    <p className="info-value">
                      {new Date(subscriptionUntilState).toLocaleDateString()}
                    </p>
                  </div>
                )}
                
                <div>
                  <p className="info-label">Research Size</p>
                  <p className="info-value">
                    Up to {SUBSCRIPTION_LIMITS.MAX_QUESTIONS} questions
                  </p>
                </div>
                
                <div>
                  <p className="info-label">Personas</p>
                  <p className="info-value">
                    Up to {SUBSCRIPTION_LIMITS.MAX_PERSONAS} personas
                  </p>
                </div>
              </div>
              
              {subscriptionCanceledDateState ? (
                <div className="subscription-alert alert-warning">
                  <p>
                    Your subscription has been canceled but remains active until {new Date(subscriptionUntilState).toLocaleDateString()}. 
                    You will continue to receive weekly credit refreshes until this date.
                  </p>
                </div>
              ) : (
                <div className="subscription-alert alert-success">
                  <p>
                    Your subscription will automatically renew. You can cancel anytime and will maintain access until the end of the current billing period.
                  </p>
                </div>
              )}
              
              <div style={{ textAlign: "right" }}>
                {!subscriptionCanceledDateState ? (
                  <button 
                    className="button secondary-button"
                    onClick={handleCancelSubscription}
                    disabled={isLoading}
                    style={{ 
                      padding: "14px 28px", 
                      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                      transition: "all 0.3s ease",
                      background: "linear-gradient(105deg, rgb(255, 182, 182), rgb(255, 234, 234))",
                      color: "rgb(124, 70, 70)",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      transform: "translateY(0)",
                      border: "none",
                      position: "relative",
                      overflow: "hidden",
                      animation: "pulse 2s infinite"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-3px)"}
                    onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    {isLoading ? 'Processing...' : 'Cancel Subscription'}
                  </button>
                ) : (
                  <button 
                    className="button standout-button account-button"
                    onClick={handleReactivateSubscription}
                    disabled={isLoading}
                    style={{ 
                      padding: "14px 28px", 
                      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                      transition: "all 0.3s ease",
                      background: "linear-gradient(135deg, var(--tertiary-color), var(--secondary-color))",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      transform: "translateY(0)",
                      border: "none",
                      position: "relative",
                      overflow: "hidden",
                      animation: "pulse 2s infinite"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-3px)"}
                    onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    {isLoading ? 'Processing...' : 'Reactivate Subscription'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ borderTop: "1px solid #eee", paddingTop: "1.5rem" }}>
              <div className="subscription-header">
                <h3 className="subscription-title">Free Research Plan</h3>
                <span className="subscription-badge badge-free">FREE</span>
              </div>
              
              <div className="subscription-grid">
                <div>
                  <p className="info-label">Credits</p>
                  <p className="info-value">
                    {creditsState} of 3 per week
                  </p>
                  <p className="info-label" style={{marginTop:"1rem"}}>Next Free Research</p>
                  <p className="info-value">
                    {getNextFreeResearchDate()}
                  </p>
                </div>

                {hasActiveSubscription ? (
                  <>
                    <div>
                      <p className="info-label">Research Size</p>
                      <p className="info-value">
                        Up to {SUBSCRIPTION_LIMITS.MAX_QUESTIONS} questions
                      </p>
                    </div>
                    
                    <div>
                      <p className="info-label">Personas</p>
                      <p className="info-value">
                        Up to {SUBSCRIPTION_LIMITS.MAX_PERSONAS} personas
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="info-label">Research Size</p>
                      <p className="info-value">
                        Up to {FREE_RESEARCH_LIMITS.MAX_QUESTIONS} questions
                      </p>
                    </div>
                    
                    <div>
                      <p className="info-label">Personas</p>
                      <p className="info-value">
                        Up to {FREE_RESEARCH_LIMITS.MAX_PERSONAS} personas
                      </p>
                    </div>
                  </>
                )}
                
              </div>
              
              {showSubscribeForm ? (
                <div className="subscription-form" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  marginTop: '2rem',
                  border: 'none',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  textAlign: 'center',
                  maxWidth: '700px',
                  margin: '2rem auto'
                }}>
                  <div style={{ 
                    marginBottom: '1.5rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '30px',
                    background: 'linear-gradient(135deg, rgba(100, 148, 112, 0.1), rgba(224, 240, 228, 0.3))',
                    display: 'inline-block'
                  }}>
                    <h3 className="subscription-title" style={{ margin: '0.5rem 0', fontSize: '1.5rem' }}>
                      Premium Subscription
                    </h3>
                  </div>
                  
                  <div style={{ 
                    marginBottom: '2rem',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '1.5rem'
                  }}>
                    <p style={{ 
                      fontWeight: "bold", 
                      fontSize: "2rem", 
                      margin: "0",
                      color: 'var(--tertiary-color)'
                    }}>€20<span style={{ fontSize: '1.2rem', color: '#777' }}>/month</span></p>
                    <p style={{ margin: '0.5rem 0 0', color: '#666' }}>Unlimited access to all premium features</p>
                  </div>
                  
                  <StripeWrapper>
                    <PaymentForm 
                      amount={20}
                      onSuccess={handleSubscriptionCheckout}
                      onError={handlePaymentError}
                      selectedPlan="SUBSCRIPTION"
                    />
                    <button 
                      className="button"
                      onClick={() => setShowSubscribeForm(false)}
                      style={{ 
                        marginTop: "1.5rem",
                        background: "transparent",
                        border: "1px solid #ddd",
                        color: "#666",
                        borderRadius: "6px",
                        padding: "10px 20px",
                        fontWeight: "500",
                        transition: "all 0.3s ease",
                        boxShadow: "none"
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = "#f5f5f5";
                        e.currentTarget.style.borderColor = "#ccc";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = "#ddd";
                      }}
                    >
                      Cancel
                    </button>
                  </StripeWrapper>
                </div>
              ) : (
                <div className="subscription-cta">
                  
                  <button 
                    className="button standout-button"
                    onClick={() => setShowSubscribeForm(true)}
                    style={{ 
                      padding: "14px 28px", 
                      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                      transition: "all 0.3s ease",
                      background: "linear-gradient(135deg, var(--tertiary-color), var(--secondary-color))",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      transform: "translateY(0)",
                      border: "none",
                      position: "relative",
                      overflow: "hidden",
                      animation: "pulse 2s infinite"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-3px)"}
                    onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    Subscribe Now
                  </button>
                  <p style={{ fontSize: "1.1rem", color: "#555", margin: "0 0 1.2rem" }}>
                    Upgrade to premium for unlimited access to research tools
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
  
        <div className="account-card danger-zone">
          <h3 className="danger-title">Delete Account</h3>
          <p>
            Warning: This action cannot be undone. <br /> All your data will be permanently deleted. <br />
            {hasActiveSubscription && " Your subscription will be cancelled immediately."}
          </p>
          
          {!showDeleteConfirm ? (
            <button 
              className="button delete-button"
              onClick={() => setShowDeleteConfirm(true)}
              
            >
              Delete Account
            </button>
          ) : (
            <div className="delete-confirm">
              <p>Are you sure you want to delete your account?</p>
              <div className="delete-actions" style={{ justifyContent: 'center' }}>
                <button 
                  className="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteInProgress}
                  style={{
                    borderRadius: '8px',
                    fontWeight: '600',
                    padding: '10px 20px',
                    minWidth: '120px',
                    boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                    background: "linear-gradient(135deg, var(--tertiary-color), var(--secondary-color))",
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="button"
                  onClick={handleDeleteAccount}
                  disabled={deleteInProgress}
                  style={{
                    background: "linear-gradient(105deg, rgb(255, 182, 182), rgb(255, 234, 234))",
                    color: "rgb(124, 70, 70)",
                    borderRadius: '8px',
                    fontWeight: '600',
                    padding: '10px 20px',
                    minWidth: '120px',
                    boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                    transition: "color 0.3s ease"
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = "rgb(124, 70, 70)"}
                  onMouseOut={(e) => e.currentTarget.style.color = "white"}
                >
                  {deleteInProgress ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
              {deleteError && <p style={{ color: "#dc3545", marginTop: "0.5rem" }}>{deleteError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyAccountPage;